import { useEffect, useMemo, useState } from 'react';
import { IconBell, IconBriefcase, IconChecklist, IconDots, IconPlus, IconSearch, IconStopwatch, IconX } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, toggleOne, type ScreenState } from '@hana/ui';
import { defOf, kpiOf, leftLabel, leftShort, openCount, selectTasks, stepOf, totals } from './logic';
import type { CustomsLane, ServiceTask, SvcCode, SvcKpi, SvcStatus } from './types';
import { ServiceCompact } from './ServiceCompact';
import { ServiceDock, type DockTab } from './ServiceDock';
import { useT } from '@hana/i18n';
import s from './services.module.css';
import './svclist.i18n';

// Mint-Service (1280 / 1536 / 1920): tab dịch vụ · Của tôi / Cả team · Bảng / Kanban / Thống kê · tìm + Lọc · bảng 8 cột.
// Chỉ đọc; nút ghi báo toast. Không có tiền ở màn này.

const ME = 'HOATTN';
const MAIN_TABS: readonly SvcCode[] = ['CCF', 'COO', 'SCY'];
const MORE_TABS: readonly SvcCode[] = ['FUM', 'PTF', 'ISR', 'DDL', 'TRF'];
type Tab = 'all' | SvcCode;
type View = 'table' | 'kanban' | 'stats';

const ST_CLS: Record<SvcStatus, string> = { New: 'stNew', Processing: 'stPro', 'Chờ bên ngoài': 'stWait', Finish: 'stFin' };
const ST_DOT: Record<SvcStatus, string> = { New: 'var(--d-amber)', Processing: 'var(--d-blue)', 'Chờ bên ngoài': 'var(--d-violet)', Finish: 'var(--d-green)' };
const KPI_CLS: Record<SvcKpi, string> = { 'Trễ': 'kErr', 'Đúng hạn': 'kOk', 'Sắp hạn': 'kWarn', 'Dừng': 'kStop', 'Đang chạy': 'kRun' };
const LANE_CLS: Record<Exclude<CustomsLane, ''>, string> = { Xanh: 'lOk', Vàng: 'lWarn', Đỏ: 'lErr', 'Soi chiếu': 'lVio' };

/** Rộng cột theo board (CW 1240): chọn 44 · job 210 · dịch vụ 210 · bước 240 · người 130 · hạn 160 · KPI 100 · trạng thái 150. */
const COLS = [
  { key: 'job', w: 210 },
  { key: 'sv', w: 210 },
  { key: 'step', w: 240 },
  { key: 'who', w: 130 },
  { key: 'due', w: 160 },
  { key: 'kpi', w: 100 },
  { key: 'st', w: 150 },
] as const;
const TOTAL = 44 + COLS.reduce((a, c) => a + c.w, 0);
const pct = (w: number) => `${((w / TOTAL) * 100).toFixed(3)}%`;

const FILTERS = [
  { key: 'who', opts: ['HOATTN', 'NHIDTT', 'ANHLT', 'NGOCNTB'] },
  { key: 'kpi', opts: ['Đang chạy', 'Sắp hạn', 'Trễ', 'Dừng', 'Đúng hạn'] },
  { key: 'status', opts: ['New', 'Processing', 'Chờ bên ngoài', 'Finish'] },
] as const;
type FKey = (typeof FILTERS)[number]['key'];

function useWide() {
  const q = '(min-width: 1800px)';
  const [w, setW] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setW(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return w;
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function ServiceTable() {
  const t = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('svcTask', { limit: 5000 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('all');
  const [moreOpen, setMoreOpen] = useState(false);
  const [scope, setScope] = useState<'mine' | 'team'>('team');
  const [view, setView] = useState<View>('table');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Record<FKey, string[]>>({ who: [], kpi: [], status: [] });
  const [filterOpen, setFilterOpen] = useState(false);
  const wide = useWide();
  const [dock, setDock] = useState<DockTab | null>('task');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const all = data?.items ?? [];
  const base = useMemo(
    () => selectTasks(all, { me: ME, scope, tab: 'all', text: q }).filter((r) => (['who', 'kpi', 'status'] as const).every((k) => !filter[k].length || filter[k].includes(k === 'who' ? r.who : k === 'kpi' ? kpiOf(r) : r.status))),
    [all, scope, q, filter],
  );
  const rows = useMemo(() => (tab === 'all' ? base : base.filter((r) => r.code === tab)), [base, tab]);
  const sum = totals(rows);
  const hasFilter = Object.values(filter).some((v) => v.length);

  const noWrite = () => toast({ msg: t('svl.noWrite') });
  const soon = (what: string) => () => toast({ msg: t('svl.soon', { what }) });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const ready = state === 'ready';

  const inMore = (MORE_TABS as readonly string[]).includes(tab);
  const cnt = (k: Tab) => (ready ? openCount(base, k) : undefined);
  const items = [{ key: 'all', label: t('svl.all'), n: cnt('all') }, ...MAIN_TABS.map((k) => ({ key: k as string, label: k, n: cnt(k) }))];
  const pickTab = (k: string) => {
    setMoreOpen(false);
    setSelected(new Set());
    setTab(k as Tab);
  };
  const toggleFilter = (f: FKey, v: string) => setFilter((cur) => ({ ...cur, [f]: cur[f].includes(v) ? cur[f].filter((x) => x !== v) : [...cur[f], v] }));
  const pickView = (v: View) => {
    if (v !== 'table') toast({ msg: t('svl.soon', { what: v === 'kanban' ? t('svl.kanban') : t('svl.stats') }) });
    else setView(v);
  };

  return (
    <Page kind="table" dock={wide && dock && ready ? <ServiceDock rows={all} me={ME} tab={dock} onTab={setDock} onClose={() => setDock(null)} /> : undefined}>
      <PageHeader
        title={t('svl.title')}
        tabs={
          <div className={s.tabs}>
            <Segmented label={t('svl.title')} value={inMore ? '' : tab} onChange={pickTab} items={items} />
            <button type="button" className={`${s.more} ${inMore ? s.moreOn : ''}`} aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
              {inMore ? tab : t('svl.more')} ▾
            </button>
            {moreOpen && (
              <div className={s.pop} role="menu" aria-label={t('svl.moreLabel')}>
                {MORE_TABS.map((k) => (
                  <button key={k} type="button" role="menuitem" className={s.popItem} onClick={() => pickTab(k)}>
                    {k} · {defOf(k).name}
                    <span className={s.popN}>{cnt(k)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        }
        actions={
          <>
            <HeaderTool label={t('svl.tool.task')} icon={IconChecklist} n={5} active={wide && dock === 'task'} onClick={wide ? () => setDock((d) => (d === 'task' ? null : 'task')) : soon(t('svl.tool.task'))} />
            <HeaderTool label={t('svl.tool.noti')} icon={IconBell} n={2} active={wide && dock === 'noti'} onClick={wide ? () => setDock((d) => (d === 'noti' ? null : 'noti')) : soon(t('svl.tool.noti'))} />
            <HeaderTool label={t('svl.tool.more')} icon={IconDots} onClick={soon(t('svl.tool.moreMenu'))} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {t('svl.add')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        {state === 'noperm' && (
          <div role="note" className={s.note}>
            <IconBriefcase size={16} aria-hidden="true" />
            {t('svl.readonly')}
          </div>
        )}
        <div className={s.tb}>
          <label className={s.search}>
            <IconSearch size={16} aria-hidden="true" />
            <input aria-label={t('svl.searchLabel')} placeholder={t('svl.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <Segmented kind="toggle" label={t('svl.scope')} value={scope} onChange={setScope} items={[{ key: 'mine', label: t('svl.mine') }, { key: 'team', label: t('svl.team') }]} />
          <Segmented kind="toggle" label={t('svl.view')} value={view} onChange={pickView} items={[{ key: 'table', label: t('svl.table') }, { key: 'kanban', label: t('svl.kanban') }, { key: 'stats', label: t('svl.stats') }]} />
          <div className={s.filterWrap}>
            <button type="button" className={s.filter} aria-expanded={filterOpen} aria-haspopup="dialog" onClick={() => setFilterOpen((o) => !o)}>
              <IconPlus size={16} aria-hidden="true" />
              {t('svl.filter')}
            </button>
            {filterOpen && (
              <div className={s.fpop} role="dialog" aria-label={t('svl.filterLabel')}>
                {FILTERS.map((f) => (
                  <div key={f.key} className={s.fgrp}>
                    <span className={s.fk}>{t(`svl.f.${f.key}`)}</span>
                    {f.opts.map((o) => (
                      <button key={o} type="button" className={`${s.fopt} ${filter[f.key].includes(o) ? s.fon : ''}`} aria-pressed={filter[f.key].includes(o)} onClick={() => toggleFilter(f.key, o)}>
                        {o}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          {hasFilter && (
            <button type="button" className={s.clear} onClick={() => setFilter({ who: [], kpi: [], status: [] })}>
              <IconX size={14} aria-hidden="true" />
              {t('svl.clear')}
            </button>
          )}
          {ready && (
            <div className={s.sum}>
              <b>{t('svl.open', { n: sum.open })}</b>
              <span>{t('svl.late')} <b className={s.err}>{sum.late}</b></span>
              <span>{t('svl.onTime')} <b className={s.okTx}>{sum.onTime}</b></span>
            </div>
          )}
        </div>

        <section aria-label={state === 'empty' || state === 'error' ? t('svl.listLabel') : undefined}>
        <StateView
          state={ready && rows.length === 0 ? 'empty' : state === 'noperm' ? 'ready' : state}
          title={ready ? t('svl.noMatch') : state === 'empty' ? t('svl.empty') : undefined}
          desc={ready ? t('svl.noMatchDesc') : state === 'empty' ? t('svl.emptyDesc') : undefined}
          icon={ready || state === 'empty' ? <IconStopwatch size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('svl.retry')}</Button> : state === 'empty' ? <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('svl.addToJob')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          <section className={s.card} aria-label={t('svl.listLabel')}>
            <table className={`${s.table} ${selected.size ? s.picking : ''}`} aria-label={t('svl.tableLabel')} aria-rowcount={rows.length + 1}>
              <colgroup>
                <col style={{ width: pct(44) }} />
                {COLS.map((c) => (
                  <col key={c.key} style={{ width: pct(c.w) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className={s.selCell}>
                    <span className={s.sr}>{t('svl.select')}</span>
                  </th>
                  {COLS.map((c) => (
                    <th key={c.key} scope="col">{t(`svl.col.${c.key}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <TaskRow key={r.id} r={r} index={i} on={selected.has(r.id)} onPick={() => setSelected(toggleOne(selected, r.id))} onOpen={soon(t('svl.detail'))} />
                ))}
              </tbody>
            </table>
            <div className={s.foot}>
              <span>{t('svl.foot1', { n: rows.length })}</span>
              <span>{t('svl.foot2')}</span>
            </div>
          </section>
        </StateView>
        </section>
      </div>
    </Page>
  );
}

function TaskRow({ r, index, on, onPick, onOpen }: { r: ServiceTask; index: number; on: boolean; onPick: () => void; onOpen: () => void }) {
  const t = useT();
  const kpi = kpiOf(r);
  const { no, name } = stepOf(r);
  const n = defOf(r.code).steps.length;
  const leftTone = r.status === 'Finish' ? undefined : r.left < 0 ? s.err : r.left <= 2 ? s.warnTx : undefined;
  return (
    <tr className={on ? s.rowOn : undefined} aria-rowindex={index + 2} aria-selected={on} title={r.urgent ? t('svl.urgent', { why: r.urgentWhy }) : undefined} onClick={onOpen}>
      <td className={s.selCell} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className={s.check} aria-label={t('svl.selectRow', { job: r.job })} checked={on} onChange={onPick} />
      </td>
      <td>
        <span className={s.job}>{r.job}</span>
        <div className={s.sub}>{r.customer}</div>
      </td>
      <td>
        <span className={s.code}>{r.code}</span>
        <div className={s.sub}>{r.id} · {r.ref}</div>
      </td>
      <td>
        <div className={s.bar} aria-hidden="true">
          {Array.from({ length: n }, (_, k) => (
            <i key={k} className={k < r.step || r.status === 'Finish' ? s.bDone : k === r.step ? (r.left < 0 ? s.bLate : s.bCur) : undefined} />
          ))}
        </div>
        <div className={s.stepTx}>
          <b>{no}</b> · {name}
          {r.lane && <span className={`${s.lane} ${s[LANE_CLS[r.lane]]}`}> {r.lane}</span>}
        </div>
      </td>
      <td>
        <span className={s.person}>
          <span className={s.ab} aria-hidden="true">{r.who.slice(0, 2)}</span>
          {r.who}
        </span>
        {r.auto && <div className={s.sub}>{t('svl.auto')}</div>}
      </td>
      <td>
        <span title={r.due}>{r.due.replace(/\/20\d\d /, ' ')}</span>
        <div className={`${s.sub} ${s.left} ${leftTone ?? ''}`} title={leftLabel(r)}>
          <IconStopwatch size={12} aria-hidden="true" />
          {leftShort(r)}
        </div>
      </td>
      <td>
        <span className={`${s.kpi} ${s[KPI_CLS[kpi]]}`}>{kpi}</span>
      </td>
      <td>
        <span className={`${s.st} ${s[ST_CLS[r.status]]}`}>
          <span className={s.stDot} style={{ background: ST_DOT[r.status] }} aria-hidden="true" />
          {r.status}
        </span>
      </td>
    </tr>
  );
}

export function ServiceList() {
  const layout = useLayout();
  return layout === 'pad' || layout === 'phone' ? <ServiceCompact phone={layout === 'phone'} /> : <ServiceTable />;
}
